import { useMemo, createContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBook, faBuilding, faCalendar, faFolder } from "@fortawesome/free-solid-svg-icons";

import MultipleTabs from "../../../reusable-components/MultipleTabs/MultipleTabs";
import { Panel } from "../../../reusable-components/MultipleTabs/interfaces";

import FiscalYearCompliance from "./FiscalYearCompliance/FiscalYearCompliance";
import AssembliesAuditsCorporateChanges from "./AssembliesAuditsCorporateChanges/AssembliesAuditsCorporateChanges";
import RegistrationsDocumentsIdsPoas from "./RegistrationsDocumentsIdsPoas/RegistrationsDocumentsIdsPoas";
import Passwords from "./Passwords/Passwords";

import useQuery from "../../../custom-hooks/useQuery/useQuery";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";

import {
  LegalEntityQuickfilter,
  useLegalEntityQuickFilter,
} from "../../../containers/Quickfilters/LegalEntityQuickfilter";

import { LegalEntityOptionValue } from "../../../containers/Quickfilters/LegalEntityQuickfilter/interfaces";
import { CompanyLegalEntityDTO } from "../../../models/company-accounts/company-legal-entities";
import { isTabAuthorized } from "src/custom-hooks/useAuthorization/useAuthorization";
import { ComplianceDocumentsLinks } from "src/widgetLink";
import { useAppSelector } from "src/custom-hooks/redux/redux";
import { NotAuthorizedPage } from "../../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useChangeYear from "../../../containers/FilterByYear/useChangeYear/useChangeYear";
import FilterByYear from "../../../containers/FilterByYear/FilterByYear";
import LoadingOverlay from "src/containers/LoadingOverlay/LoadingOverlay";

interface Filter {
  legalEntity: {
    legalEntityOption: LegalEntityOptionValue | null;
    selectedLegalEntity: CompanyLegalEntityDTO | null;
    legalEntityQuickFilter: JSX.Element | null;
    legalEntities: CompanyLegalEntityDTO[];
  };
  year: {
    yearValue: number;
    yearPicker: JSX.Element | null;
  };
}

export const ComplianceDocumentsContext = createContext<Filter>({
  legalEntity: {
    legalEntityOption: null,
    selectedLegalEntity: null,
    legalEntityQuickFilter: null,
    legalEntities: [],
  },
  year: {
    yearValue: new Date().getFullYear(),
    yearPicker: null,
  },
});

export enum ComplianceRoutes {
  COMPLIANCE = "compliance",
  ASSEMBLIES = "assemblies",
  REGISTRATIONS = "registrations",
  PASSWORDS = "passwords",
}

const ComplianceDocuments = () => {
  const { t } = useTranslation();
  const tRP = "pages.document_reports.compliance_documents"; // tRP - translation root path

  const {
    legalEntitiesQuickFilter,
    setLegalEntitiesQuickFilter,
    selectedLegalEntity,
    legalEntityQFIsLoading,
    allLegalEntities,
  } = useLegalEntityQuickFilter();

  const { year, handleChangeYear } = useChangeYear();

  const { currentUserAuthorizations, currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const checkPanelAccess = useCallback((showPanel: boolean, panel: Panel) => (showPanel ? [panel] : []), []);

  const hasAccessToComplianceDocuments = useAuthorization([
    AUTHORIZATIONS.LegalEntityAccess.RunAccountingReports,
    AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting,
    AUTHORIZATIONS.LegalEntityAccess.ViewComplianceData,
  ]);

  const hasAccessToPasswords = useAuthorization([
    AUTHORIZATIONS.LegalEntityAccess.ViewLegalEntitySI,
    AUTHORIZATIONS.LegalEntityAccess.ManageLegalEntitySI,
  ]);

  const allowCompliance =
    isTabAuthorized(ComplianceDocumentsLinks.Compliance, currentUserAuthorizations, currentUserData) ||
    hasAccessToComplianceDocuments;

  const allowAssemblies =
    isTabAuthorized(ComplianceDocumentsLinks.Assemblies, currentUserAuthorizations, currentUserData) ||
    hasAccessToComplianceDocuments;

  const allowRegistrations =
    isTabAuthorized(ComplianceDocumentsLinks.Registrations, currentUserAuthorizations, currentUserData) ||
    hasAccessToComplianceDocuments;

  const allowPasswords =
    isTabAuthorized(ComplianceDocumentsLinks.Passwords, currentUserAuthorizations, currentUserData) ||
    hasAccessToPasswords;

  let query = useQuery();

  const panels: Panel[] = useMemo(
    () => [
      ...checkPanelAccess(allowCompliance, {
        tabLabel: t(`${tRP}.fiscal_year_compliance.tab_label`),
        tabIcon: <FontAwesomeIcon icon={faBuilding} />,
        tabPanelComponent: (
          <FiscalYearCompliance
            authDomainToHideDoc={AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting}
            authDomainToSetVerified={AUTHORIZATIONS.LegalEntityAccess.ProcessIncomingDocuments}
            allowAuthorizations={[AUTHORIZATIONS.LegalEntityAccess.ProcessAccounting]}
          />
        ),
        routePath: ComplianceRoutes.COMPLIANCE,
        tableTitle: t(`${tRP}.fiscal_year_compliance.tab_label`),
      }),
      ...checkPanelAccess(allowAssemblies, {
        tabLabel: t(`${tRP}.assemblies_audits_corporate_changes.tab_label`),
        tabIcon: <FontAwesomeIcon icon={faFolder} />,
        tabPanelComponent: <AssembliesAuditsCorporateChanges />,
        routePath: ComplianceRoutes.ASSEMBLIES,
        tableTitle: t(`${tRP}.assemblies_audits_corporate_changes.tab_label`),
      }),
      ...checkPanelAccess(allowRegistrations, {
        tabLabel: t(`${tRP}.registrations_documents.tab_label`),
        tabIcon: <FontAwesomeIcon icon={faBook} />,
        tabPanelComponent: <RegistrationsDocumentsIdsPoas />,
        routePath: ComplianceRoutes.REGISTRATIONS,
        tableTitle: t(`${tRP}.registrations_documents.tab_label`),
      }),
      ...checkPanelAccess(allowPasswords, {
        tabLabel: t(`${tRP}.passwords.tab_label`),
        tabIcon: <FontAwesomeIcon icon={faCalendar} />,
        tabPanelComponent: <Passwords />,
        routePath: ComplianceRoutes.PASSWORDS,
        tableTitle: t(`${tRP}.passwords.tab_label`),
      }),
    ],
    [t]
  );

  const selectedTab = useMemo(() => {
    let found = panels.findIndex((panel) => panel.routePath === query.get("tab"));
    found = found >= 0 ? found : 0;
    return found;
  }, [query, panels]);

  return (
    <>
      {panels.length ? (
        <ComplianceDocumentsContext.Provider
          value={{
            legalEntity: {
              legalEntityOption: legalEntitiesQuickFilter.legalEntities,
              selectedLegalEntity: selectedLegalEntity,
              legalEntities: allLegalEntities,
              legalEntityQuickFilter: (
                <LegalEntityQuickfilter
                  quickFilters={legalEntitiesQuickFilter}
                  handleChangeQuickFilters={setLegalEntitiesQuickFilter}
                  allLegalEntitiesOption
                  allLegalEntities={allLegalEntities}
                />
              ),
            },
            year: {
              yearValue: year,
              yearPicker: (
                <FilterByYear
                  year={year}
                  handleChangeYear={handleChangeYear}
                  filterColor={"filter"}
                />
              ),
            },
          }}
        >
          <LoadingOverlay loading={legalEntityQFIsLoading}>
            {!legalEntityQFIsLoading && (
              <MultipleTabs
                panels={panels}
                selectedTab={selectedTab}
              />
            )}
          </LoadingOverlay>
        </ComplianceDocumentsContext.Provider>
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};

export default ComplianceDocuments;
